import { memo } from "react";
import { X, FolderOpen } from "lucide-react";
import { MATERIALS } from "../../lab/materials";
import { useSaved } from "../../lab/store";
import { ParamControl, ActionButton, RangeControl, ToggleControl } from "./Controls";

export const Section = ({ title, children, testid }) => (
  <section className="panel-sec" data-testid={testid}>
    <h3 className="panel-title">{title}</h3>
    <div className="space-y-4">{children}</div>
  </section>
);

const ModeSection = ({ mode, params, setParam, runAction }) => {
  const groups = {};
  mode.params.forEach((p) => { (groups[p.section || "Experiment parameters"] ||= { params: [], actions: [] }).params.push(p); });
  mode.actions.forEach((a) => { (groups[a.section || "Experiment parameters"] ||= { params: [], actions: [] }).actions.push(a); });
  return Object.entries(groups).map(([title, g]) => (
    <Section key={title} title={title} testid={`panel-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      {g.actions.length > 0 && <div className="flex flex-wrap gap-2">{g.actions.map((a) => <ActionButton key={a.id} action={a} onRun={runAction} />)}</div>}
      {g.params.map((p) => <ParamControl key={p.key} p={p} value={params[p.key]} onChange={setParam} />)}
    </Section>
  ));
};

const MaterialPicker = ({ value, onChange }) => (
  <div className="ctl">
    <div className="ctl-head"><span>Pin material</span><span className="ctl-val">{MATERIALS[value].label}</span></div>
    <div className="grid grid-cols-4 gap-2">
      {Object.entries(MATERIALS).map(([k, m]) => (
        <button key={k} data-testid={`material-${k}-button`} title={m.label} onClick={() => onChange(k)} className={`mat-btn ${value === k ? "is-on" : ""}`}>
          <span className="mat-swatch" style={{ background: m.swatch }} />
          <span className="mat-label">{m.short}</span>
        </button>
      ))}
    </div>
  </div>
);

export const SavedList = ({ instrument, onOpen }) => {
  const saved = useSaved(instrument);
  return (
    <Section title="Saved experiments" testid="panel-section-saved">
      {saved.length === 0 ? <p className="panel-desc text-xs" data-testid="saved-empty">Nothing saved yet — use Save in the toolbar.</p> :
        saved.map((s) => (
          <button key={s.key} className="saved-row" onClick={() => onOpen(s)} data-testid={`saved-open-${s.key}`}>
            <FolderOpen size={14} /><span className="flex-1 text-left">{s.title}</span><span className="ctl-val">{s.at.toLocaleTimeString()}</span>
          </button>
        ))}
    </Section>
  );
};

export const ControlPanel = memo(function ControlPanel({ open, onClose, ins, mode, params, setParam, runAction, settings: s, setSetting, onOpenSaved }) {
  const pins = ins.style === "pins";
  return (
    <aside className={`ctl-panel holo ${open ? "is-open" : ""}`} data-testid="control-panel">
      <div className="ctl-scroll">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="panel-title">Instrument {ins.code}</div>
            <h2 className="panel-mode holo-text" data-testid="panel-mode-name">{ins.name}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="panel-close-button" aria-label="Close settings"><X size={16} /></button>
        </div>
        <p className="panel-desc">{mode.desc}</p>
        <div className="panel-math" data-testid="panel-mode-equation">{mode.math}</div>
        <ModeSection mode={mode} params={params} setParam={setParam} runAction={runAction} />
        <Section title="Simulation" testid="panel-section-simulation">
          <RangeControl label="Simulation speed" value={s.simSpeed} min={0.1} max={3} step={0.05} onChange={(v) => setSetting("simSpeed", v)} testid="slider-simulation-speed" />
          <RangeControl label="Force intensity" value={s.force} min={0.1} max={2.5} step={0.05} onChange={(v) => setSetting("force", v)} testid="slider-force-intensity" />
          <RangeControl label={pins ? "Pin movement speed" : "Surface response speed"} value={s.speed} min={0.2} max={3} step={0.05} onChange={(v) => setSetting("speed", v)} testid="slider-pin-speed" />
        </Section>
        <Section title={pins ? "Pin board" : "Instrument surface"} testid="panel-section-board">
          <RangeControl label={pins ? "Pin density (per side)" : "Surface resolution"} value={s.density} min={32} max={160} step={8} onChange={(v) => setSetting("density", v)} testid="slider-pin-density" />
          <RangeControl label={pins ? "Pin height / travel" : "Relief height"} value={s.pinHeight} min={0.3} max={2.5} step={0.05} onChange={(v) => setSetting("pinHeight", v)} testid="slider-pin-height" />
          {pins && <MaterialPicker value={s.material} onChange={(v) => setSetting("material", v)} />}
          <RangeControl label="Lighting intensity" value={s.light} min={0.1} max={2.5} step={0.05} onChange={(v) => setSetting("light", v)} testid="slider-lighting-intensity" />
          <ToggleControl label="Data colouring" value={s.colorize} onChange={(v) => setSetting("colorize", v)} testid="toggle-colorize" />
        </Section>
        <SavedList instrument={ins.id} onOpen={onOpenSaved} />
        <div className="panel-foot">Left-drag interacts · Right-drag orbits · Scroll zooms · Space pauses · V swaps view · Esc returns to the laboratory</div>
      </div>
    </aside>
  );
});
